import "./style.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { CloseOutlined, EastOutlined } from "@mui/icons-material";
import EmptyState from "components/shared/EmptyState";
import { getCognitoUserAttribute } from "helpers/utils";
import useBindingKeyboard from "hooks/useBindingKeyboard";
import { useIsMobile } from "hooks/useIsMobile";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { markNotifAsSeen } from "store/notificationStore";
import { useAppDispatch, useAppSelector } from "store/store";

import Notification from "./Notification";

type TProps = {
    close: () => void;
};

const NotificationCenter = ({ close }: TProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { isMobile } = useIsMobile();
    const { notifications } = useAppSelector((state) => state.notification);

    const { user } = useAuthenticator((context) => [context.user]);
    const userAttributes = getCognitoUserAttribute("custom:accountType", user);
    const baseURL =
        userAttributes === "beneficiary"
            ? import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT
            : ["publisher", "admin"].includes(userAttributes)
              ? import.meta.env.REACT_APP_API_ENDPOINT
              : "";

    const handleMarkAllAsRead = () => {
        if (!!baseURL && !!notifications.filter((notif) => !notif.seen).length) {
            dispatch(
                markNotifAsSeen({
                    baseUrl: baseURL,
                    id: notifications.filter((notif) => !notif.seen).map((notif) => notif.id),
                }),
            );
        }
    };

    const handleClose = useCallback(
        (ev: KeyboardEvent) => {
            close();
        },
        [close],
    );

    useBindingKeyboard({
        action: handleClose,
        type: "keyup",
        touchToBind: ["Escape"],
    });

    return (
        <div className="notification-center" onClick={(e) => e.stopPropagation()}>
            <div className="notification-header">
                <h3>
                    {isMobile ? t("Notifications.Notifications") : t("Notifications.TitleHeader")} (
                    {notifications.filter((notif) => !notif.seen).length})
                </h3>
                <div style={{ display: "flex", alignItems: "center" }}>
                    {!!notifications.filter((notif) => !notif.seen).length && (
                        <button className="mark-all-as-read" onClick={() => handleMarkAllAsRead()}>
                            {t("Notifications.MarkAllAsRead")}
                        </button>
                    )}
                    <button onClick={close}>
                        <CloseOutlined />
                    </button>
                </div>
            </div>
            <ul className="notification-body">
                {notifications.filter((notif) => !notif.seen).length > 0 ? (
                    notifications
                        .filter((notif) => !notif.seen)
                        .map((notif, i) => <Notification key={`notif-${i}`} data={notif} close={close} />)
                ) : (
                    <EmptyState type="notifications" sx={{ margin: "16px auto 0" }} />
                )}
            </ul>
            {!isMobile && (
                <div className="notification-footer">
                    <Link to={"/notifications"} onClick={() => close()}>
                        <EastOutlined />
                        {t("Notifications.ViewHistory")}
                    </Link>
                </div>
            )}
        </div>
    );
};

export default NotificationCenter;
